<script lang="ts">
  interface Props {
    legend: string;
    hideLegend?: boolean;
    min: number;
    max: number;
    step: number;
    value: number | null;
    onChange: (value: number) => void;
    minLabel?: string;
    maxLabel?: string;
    formatValue?: (value: number) => string;
    describedBy?: string;
  }

  let {
    legend,
    hideLegend = false,
    min,
    max,
    step,
    value,
    onChange,
    minLabel,
    maxLabel,
    formatValue = String,
    describedBy,
  }: Props = $props();

  const MAX_SCALE_BUTTONS = 11;
  const name = $props.id();
  const safeStep = $derived(step > 0 ? step : 1);
  const count = $derived(Math.floor((max - min) / safeStep) + 1);
  const values = $derived(
    Array.from({ length: Math.min(count, MAX_SCALE_BUTTONS) }, (_, index) =>
      Number((min + index * safeStep).toFixed(4))
    )
  );

  function optionLabel(option: number): string {
    if (option === min && minLabel) return `${formatValue(option)}, ${minLabel}`;
    if (option === max && maxLabel) return `${formatValue(option)}, ${maxLabel}`;
    return formatValue(option);
  }
</script>

{#snippet ends()}
  <div class="stat-scale__ends" aria-hidden="true">
    <span>{minLabel ? `${formatValue(min)} · ${minLabel}` : formatValue(min)}</span>
    <span>{maxLabel ? `${formatValue(max)} · ${maxLabel}` : formatValue(max)}</span>
  </div>
{/snippet}

{#if count > MAX_SCALE_BUTTONS}
  <div class="stat-scale">
    <label class={hideLegend ? 'stat-visually-hidden' : 'stat-label'} for={name}>{legend}</label>
    <input
      id={name}
      class="stat-range"
      type="range"
      {min}
      {max}
      step={safeStep}
      value={value ?? min}
      aria-valuetext={value === null ? 'Not rated yet' : formatValue(value)}
      aria-describedby={describedBy}
      oninput={(event) => onChange(Number(event.currentTarget.value))}
    />
    {@render ends()}
    <p class="stat-meta" aria-live="polite">
      {value === null ? 'Move the slider to choose a rating.' : `Your rating: ${formatValue(value)}`}
    </p>
  </div>
{:else}
  <fieldset class="stat-scale ws-scale--fit" aria-describedby={describedBy}>
    <legend class={hideLegend ? 'stat-visually-hidden' : 'stat-label'}>{legend}</legend>
    <div class="stat-scale__options">
      {#each values as option (option)}
        <label class="stat-scale__option">
          <input
            type="radio"
            {name}
            value={option}
            checked={value === option}
            aria-label={optionLabel(option)}
            onchange={() => onChange(option)}
          />
          <span aria-hidden="true">{formatValue(option)}</span>
        </label>
      {/each}
    </div>
    {@render ends()}
  </fieldset>
{/if}
